import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Tabs,
    Tab,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Switch,
    Chip,
    Grid,
    Card,
    CardContent,
    Alert,
    Divider,
    RadioGroup,
    Radio,
    FormLabel,
    Checkbox,
    FormGroup
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Settings as SettingsIcon,
    Quiz as QuizIcon,
    Analytics as AnalyticsIcon
} from '@mui/icons-material';
import axios from 'axios';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export default function TrainingTestManagement() {
    const [tabValue, setTabValue] = useState(0);
    const [categories, setCategories] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [settings, setSettings] = useState({});
    const [analytics, setAnalytics] = useState({});
    const [analyticsFilters, setAnalyticsFilters] = useState({
        page: 1,
        limit: 20,
        startDate: '',
        endDate: '',
        userId: '',
        teamId: ''
    });
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Question form state
    const [questionDialog, setQuestionDialog] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [questionForm, setQuestionForm] = useState({
        category_id: '',
        question: '',
        type: 'multiple_choice',
        options: ['', '', '', ''],
        correct_answer: '',
        explanation: '',
        difficulty: 'medium',
        is_active: true
    });

    // Settings form state
    const [settingsForm, setSettingsForm] = useState({});

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [categoriesRes, questionsRes, settingsRes, usersRes, teamsRes] = await Promise.all([
                axios.get('/api/training-test/categories'),
                axios.get('/api/training-test/questions'),
                axios.get('/api/training-test/settings'),
                axios.get('/api/training-test/users'),
                axios.get('/api/training-test/teams')
            ]);

            setCategories(categoriesRes.data);
            setQuestions(questionsRes.data);
            setSettings(settingsRes.data);
            setSettingsForm(settingsRes.data);
            setUsers(usersRes.data);
            setTeams(teamsRes.data);

            // Load analytics separately
            await loadAnalytics();
        } catch (err) {
            setError('Failed to load data: ' + err.response?.data?.error || err.message);
        }
        setLoading(false);
    };

    const loadAnalytics = async () => {
        try {
            const params = new URLSearchParams();
            if (analyticsFilters.startDate) params.append('startDate', analyticsFilters.startDate);
            if (analyticsFilters.endDate) params.append('endDate', analyticsFilters.endDate);
            if (analyticsFilters.userId) params.append('userId', analyticsFilters.userId);
            if (analyticsFilters.teamId) params.append('teamId', analyticsFilters.teamId);
            params.append('page', analyticsFilters.page.toString());
            params.append('limit', analyticsFilters.limit.toString());

            const analyticsRes = await axios.get(`/api/training-test/analytics?${params}`);
            setAnalytics(analyticsRes.data);
        } catch (err) {
            setError('Failed to load analytics: ' + err.response?.data?.error || err.message);
        }
    };

    const loadQuestions = async () => {
        try {
            const params = selectedCategory ? { category: selectedCategory } : {};
            const response = await axios.get('/api/training-test/questions', { params });
            setQuestions(response.data);
        } catch (err) {
            setError('Failed to load questions: ' + err.response?.data?.error || err.message);
        }
    };

    useEffect(() => {
        if (categories.length > 0) {
            loadQuestions();
        }
    }, [selectedCategory]);

    const handleSaveSettings = async () => {
        setLoading(true);
        try {
            await axios.put('/api/training-test/settings', settingsForm);
            setSettings(settingsForm);
            setSuccess('Settings saved successfully');
        } catch (err) {
            setError('Failed to save settings: ' + err.response?.data?.error || err.message);
        }
        setLoading(false);
    };

    const handleOpenQuestionDialog = (question = null) => {
        if (question) {
            setEditingQuestion(question);
            setQuestionForm({
                category_id: question.category_id,
                question: question.question,
                type: question.type,
                options: question.options || ['', '', '', ''],
                correct_answer: question.correct_answer,
                explanation: question.explanation || '',
                difficulty: question.difficulty,
                is_active: question.is_active
            });
        } else {
            setEditingQuestion(null);
            setQuestionForm({
                category_id: '',
                question: '',
                type: 'multiple_choice',
                options: ['', '', '', ''],
                correct_answer: '',
                explanation: '',
                difficulty: 'medium',
                is_active: true
            });
        }
        setQuestionDialog(true);
    };

    const handleSaveQuestion = async () => {
        setLoading(true);
        try {
            const data = { ...questionForm };

            // Handle different question types
            if (data.type === 'true_false') {
                data.options = null;
            } else if (data.type === 'fill_blank') {
                data.options = null;
            } else if (data.type === 'checkbox') {
                // For checkbox, correct_answer should be an array
                if (typeof data.correct_answer === 'string') {
                    data.correct_answer = [data.correct_answer];
                }
            }

            // Remove empty options
            if (data.options) {
                data.options = data.options.filter(opt => opt.trim() !== '');
            }

            if (editingQuestion) {
                await axios.put(`/api/training-test/questions/${editingQuestion.id}`, data);
                setSuccess('Question updated successfully');
            } else {
                await axios.post('/api/training-test/questions', data);
                setSuccess('Question created successfully');
            }

            setQuestionDialog(false);
            loadQuestions();
        } catch (err) {
            setError('Failed to save question: ' + err.response?.data?.error || err.message);
        }
        setLoading(false);
    };

    const handleDeleteQuestion = async (questionId) => {
        if (!window.confirm('Are you sure you want to delete this question?')) return;

        try {
            await axios.delete(`/api/training-test/questions/${questionId}`);
            setSuccess('Question deleted successfully');
            loadQuestions();
        } catch (err) {
            setError('Failed to delete question: ' + err.response?.data?.error || err.message);
        }
    };

    const handleDeleteAttempt = async (attemptId) => {
        if (!window.confirm('Are you sure you want to delete this test attempt? This action cannot be undone.')) return;

        try {
            await axios.delete(`/api/training-test/attempt/${attemptId}`);
            setSuccess('Test attempt deleted successfully');
            await loadAnalytics();
        } catch (err) {
            setError('Failed to delete test attempt: ' + err.response?.data?.error || err.message);
        }
    };

    const renderQuestionOptions = () => {
        const { type } = questionForm;

        if (type === 'multiple_choice') {
            return (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Options (choose correct answer below)</Typography>
                    {questionForm.options.map((option, index) => (
                        <TextField
                            key={index}
                            fullWidth
                            label={`Option ${index + 1}`}
                            value={option}
                            onChange={(e) => {
                                const newOptions = [...questionForm.options];
                                newOptions[index] = e.target.value;
                                setQuestionForm({ ...questionForm, options: newOptions });
                            }}
                            sx={{ mb: 1 }}
                        />
                    ))}
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Correct Answer</InputLabel>
                        <Select
                            value={questionForm.correct_answer}
                            onChange={(e) => setQuestionForm({ ...questionForm, correct_answer: e.target.value })}
                        >
                            {questionForm.options.map((option, index) => (
                                option.trim() && (
                                    <MenuItem key={index} value={option}>
                                        {option}
                                    </MenuItem>
                                )
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            );
        }

        if (type === 'true_false') {
            return (
                <FormControl>
                    <FormLabel>Correct Answer</FormLabel>
                    <RadioGroup
                        value={questionForm.correct_answer}
                        onChange={(e) => setQuestionForm({ ...questionForm, correct_answer: e.target.value })}
                    >
                        <FormControlLabel value="true" control={<Radio />} label="True" />
                        <FormControlLabel value="false" control={<Radio />} label="False" />
                    </RadioGroup>
                </FormControl>
            );
        }

        if (type === 'checkbox') {
            return (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Options (select all correct answers)</Typography>
                    {questionForm.options.map((option, index) => (
                        <TextField
                            key={index}
                            fullWidth
                            label={`Option ${index + 1}`}
                            value={option}
                            onChange={(e) => {
                                const newOptions = [...questionForm.options];
                                newOptions[index] = e.target.value;
                                setQuestionForm({ ...questionForm, options: newOptions });
                            }}
                            sx={{ mb: 1 }}
                        />
                    ))}
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Correct Answers</Typography>
                    <FormGroup>
                        {questionForm.options.map((option, index) => (
                            option.trim() && (
                                <FormControlLabel
                                    key={index}
                                    control={
                                        <Checkbox
                                            checked={Array.isArray(questionForm.correct_answer) ?
                                                questionForm.correct_answer.includes(option) :
                                                questionForm.correct_answer === option
                                            }
                                            onChange={(e) => {
                                                let newCorrect = Array.isArray(questionForm.correct_answer) ?
                                                    [...questionForm.correct_answer] : [];

                                                if (e.target.checked) {
                                                    if (!newCorrect.includes(option)) {
                                                        newCorrect.push(option);
                                                    }
                                                } else {
                                                    newCorrect = newCorrect.filter(ans => ans !== option);
                                                }

                                                setQuestionForm({ ...questionForm, correct_answer: newCorrect });
                                            }}
                                        />
                                    }
                                    label={option}
                                />
                            )
                        ))}
                    </FormGroup>
                </Box>
            );
        }

        if (type === 'fill_blank') {
            return (
                <TextField
                    fullWidth
                    label="Correct Answer"
                    value={questionForm.correct_answer}
                    onChange={(e) => setQuestionForm({ ...questionForm, correct_answer: e.target.value })}
                    helperText="This answer will be matched case-insensitively"
                />
            );
        }

        return null;
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Paper>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                    <Tab icon={<QuizIcon />} label="Questions" />
                    <Tab icon={<SettingsIcon />} label="Settings" />
                    <Tab icon={<AnalyticsIcon />} label="Analytics" />
                </Tabs>

                {error && (
                    <Alert severity="error" onClose={() => setError('')} sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" onClose={() => setSuccess('')} sx={{ m: 2 }}>
                        {success}
                    </Alert>
                )}

                {/* Questions Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5">Training Test Questions</Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenQuestionDialog()}
                        >
                            Add Question
                        </Button>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by Category</InputLabel>
                            <Select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {categories.map(cat => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Question</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Difficulty</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {questions.map((question) => (
                                    <TableRow key={question.id}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 300 }}>
                                                {question.question.length > 100
                                                    ? question.question.substring(0, 100) + '...'
                                                    : question.question
                                                }
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{question.category_name}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={question.type.replace('_', ' ')}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={question.difficulty}
                                                size="small"
                                                color={
                                                    question.difficulty === 'easy' ? 'success' :
                                                    question.difficulty === 'hard' ? 'error' : 'warning'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={question.is_active ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={question.is_active ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenQuestionDialog(question)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteQuestion(question.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Settings Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Typography variant="h5" sx={{ mb: 3 }}>Test Settings</Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2 }}>Test Configuration</Typography>

                                    <TextField
                                        fullWidth
                                        label="Questions per Test"
                                        type="number"
                                        value={settingsForm.questions_per_test || ''}
                                        onChange={(e) => setSettingsForm({
                                            ...settingsForm,
                                            questions_per_test: e.target.value
                                        })}
                                        sx={{ mb: 2 }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Time Limit (minutes)"
                                        type="number"
                                        value={settingsForm.time_limit_minutes || ''}
                                        onChange={(e) => setSettingsForm({
                                            ...settingsForm,
                                            time_limit_minutes: e.target.value
                                        })}
                                        sx={{ mb: 2 }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Pass Percentage"
                                        type="number"
                                        value={settingsForm.pass_percentage || ''}
                                        onChange={(e) => setSettingsForm({
                                            ...settingsForm,
                                            pass_percentage: e.target.value
                                        })}
                                        sx={{ mb: 2 }}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2 }}>Anti-Cheat Settings</Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settingsForm.anti_cheat_enabled === 'true'}
                                                onChange={(e) => setSettingsForm({
                                                    ...settingsForm,
                                                    anti_cheat_enabled: e.target.checked ? 'true' : 'false'
                                                })}
                                            />
                                        }
                                        label="Enable Anti-Cheat"
                                        sx={{ mb: 2 }}
                                    />

                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Strictness Level</InputLabel>
                                        <Select
                                            value={settingsForm.anti_cheat_strictness || ''}
                                            onChange={(e) => setSettingsForm({
                                                ...settingsForm,
                                                anti_cheat_strictness: e.target.value
                                            })}
                                        >
                                            <MenuItem value="low">Low</MenuItem>
                                            <MenuItem value="medium">Medium</MenuItem>
                                            <MenuItem value="high">High</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        fullWidth
                                        label="Max Violations Allowed"
                                        type="number"
                                        value={settingsForm.max_violations_allowed || ''}
                                        onChange={(e) => setSettingsForm({
                                            ...settingsForm,
                                            max_violations_allowed: e.target.value
                                        })}
                                        sx={{ mb: 2 }}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2 }}>Display Options</Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settingsForm.show_explanations === 'true'}
                                                onChange={(e) => setSettingsForm({
                                                    ...settingsForm,
                                                    show_explanations: e.target.checked ? 'true' : 'false'
                                                })}
                                            />
                                        }
                                        label="Show Explanations After Test"
                                        sx={{ mb: 2 }}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settingsForm.randomize_questions === 'true'}
                                                onChange={(e) => setSettingsForm({
                                                    ...settingsForm,
                                                    randomize_questions: e.target.checked ? 'true' : 'false'
                                                })}
                                            />
                                        }
                                        label="Randomize Question Order"
                                        sx={{ mb: 2 }}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settingsForm.randomize_options === 'true'}
                                                onChange={(e) => setSettingsForm({
                                                    ...settingsForm,
                                                    randomize_options: e.target.checked ? 'true' : 'false'
                                                })}
                                            />
                                        }
                                        label="Randomize Answer Options"
                                        sx={{ mb: 2 }}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settingsForm.allow_review === 'true'}
                                                onChange={(e) => setSettingsForm({
                                                    ...settingsForm,
                                                    allow_review: e.target.checked ? 'true' : 'false'
                                                })}
                                            />
                                        }
                                        label="Allow Review Before Submit"
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            onClick={handleSaveSettings}
                            disabled={loading}
                            size="large"
                        >
                            Save Settings
                        </Button>
                    </Box>
                </TabPanel>

                {/* Analytics Tab */}
                <TabPanel value={tabValue} index={2}>
                    <Typography variant="h5" sx={{ mb: 3 }}>Test Analytics</Typography>

                    {/* Filter Controls */}
                    <Box sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={analyticsFilters.startDate || ''}
                                    onChange={(e) => setAnalyticsFilters({...analyticsFilters, startDate: e.target.value})}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    label="End Date"
                                    type="date"
                                    value={analyticsFilters.endDate || ''}
                                    onChange={(e) => setAnalyticsFilters({...analyticsFilters, endDate: e.target.value})}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Employee</InputLabel>
                                    <Select
                                        value={analyticsFilters.userId}
                                        onChange={(e) => setAnalyticsFilters({...analyticsFilters, userId: e.target.value, page: 1})}
                                    >
                                        <MenuItem value="">All Employees</MenuItem>
                                        {users.map(user => (
                                            <MenuItem key={user.id} value={user.id}>
                                                {user.username} ({user.name})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Team</InputLabel>
                                    <Select
                                        value={analyticsFilters.teamId}
                                        onChange={(e) => setAnalyticsFilters({...analyticsFilters, teamId: e.target.value, page: 1})}
                                    >
                                        <MenuItem value="">All Teams</MenuItem>
                                        {teams.map(team => (
                                            <MenuItem key={team.team_id} value={team.team_id}>
                                                {team.team_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={1}>
                                <FormControl fullWidth>
                                    <InputLabel>Size</InputLabel>
                                    <Select
                                        value={analyticsFilters.limit}
                                        onChange={(e) => setAnalyticsFilters({...analyticsFilters, limit: e.target.value, page: 1})}
                                    >
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={20}>20</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={1.5}>
                                <Button
                                    variant="contained"
                                    onClick={() => loadAnalytics()}
                                    fullWidth
                                >
                                    Apply
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={1.5}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setAnalyticsFilters({page: 1, limit: 20, startDate: '', endDate: '', userId: '', teamId: ''});
                                        loadAnalytics();
                                    }}
                                    fullWidth
                                >
                                    Clear
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6">Total Attempts</Typography>
                                    <Typography variant="h4" color="primary">
                                        {analytics.totalAttempts?.count || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6">Average Score</Typography>
                                    <Typography variant="h4" color="success.main">
                                        {analytics.averageScore?.avg ? Math.round(analytics.averageScore.avg) : 0}%
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6">Pass Rate</Typography>
                                    <Typography variant="h4" color="info.main">
                                        {analytics.passRate?.rate ? Math.round(analytics.passRate.rate) : 0}%
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6">Flagged Attempts</Typography>
                                    <Typography variant="h4" color="error.main">
                                        {analytics.flaggedAttempts?.count || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Recent Attempts */}
                    {analytics.recentAttempts && analytics.recentAttempts.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Recent Test Attempts</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Agent</TableCell>
                                            <TableCell>Score</TableCell>
                                            <TableCell>Pass/Fail</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Time Taken</TableCell>
                                            <TableCell>Flagged</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {analytics.recentAttempts.map((attempt) => (
                                            <TableRow key={attempt.id}>
                                                <TableCell>{attempt.username || 'Unknown'}</TableCell>
                                                <TableCell>{attempt.score}%</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={attempt.passed ? 'Pass' : 'Fail'}
                                                        color={attempt.passed ? 'success' : 'error'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(attempt.completed_at).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell>
                                                    {Math.round(attempt.time_taken / 60)} minutes
                                                </TableCell>
                                                <TableCell>
                                                    {attempt.is_flagged && (
                                                        <Chip
                                                            label="Flagged"
                                                            color="warning"
                                                            size="small"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteAttempt(attempt.id)}
                                                        title="Delete this test attempt"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination Controls */}
                            {analytics.pagination && analytics.pagination.total > analytics.pagination.limit && (
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        disabled={analyticsFilters.page <= 1}
                                        onClick={() => {
                                            setAnalyticsFilters({...analyticsFilters, page: analyticsFilters.page - 1});
                                            loadAnalytics();
                                        }}
                                    >
                                        Previous
                                    </Button>

                                    <Typography variant="body2">
                                        Page {analytics.pagination.page} of {Math.ceil(analytics.pagination.total / analytics.pagination.limit)}
                                        ({analytics.pagination.total} total)
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        disabled={analyticsFilters.page >= Math.ceil(analytics.pagination.total / analytics.pagination.limit)}
                                        onClick={() => {
                                            setAnalyticsFilters({...analyticsFilters, page: analyticsFilters.page + 1});
                                            loadAnalytics();
                                        }}
                                    >
                                        Next
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}
                </TabPanel>
            </Paper>

            {/* Question Dialog */}
            <Dialog
                open={questionDialog}
                onClose={() => setQuestionDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {editingQuestion ? 'Edit Question' : 'Add New Question'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={questionForm.category_id}
                                    onChange={(e) => setQuestionForm({ ...questionForm, category_id: e.target.value })}
                                >
                                    {categories.map(cat => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Question Type</InputLabel>
                                <Select
                                    value={questionForm.type}
                                    onChange={(e) => setQuestionForm({
                                        ...questionForm,
                                        type: e.target.value,
                                        correct_answer: '',
                                        options: e.target.value === 'multiple_choice' || e.target.value === 'checkbox'
                                            ? ['', '', '', ''] : null
                                    })}
                                >
                                    <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                                    <MenuItem value="true_false">True/False</MenuItem>
                                    <MenuItem value="checkbox">Checkbox (Multiple Answers)</MenuItem>
                                    <MenuItem value="fill_blank">Fill in the Blank</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Question"
                                value={questionForm.question}
                                onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            {renderQuestionOptions()}
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Difficulty</InputLabel>
                                <Select
                                    value={questionForm.difficulty}
                                    onChange={(e) => setQuestionForm({ ...questionForm, difficulty: e.target.value })}
                                >
                                    <MenuItem value="easy">Easy</MenuItem>
                                    <MenuItem value="medium">Medium</MenuItem>
                                    <MenuItem value="hard">Hard</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={questionForm.is_active}
                                        onChange={(e) => setQuestionForm({ ...questionForm, is_active: e.target.checked })}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Explanation (optional)"
                                value={questionForm.explanation}
                                onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
                                helperText="Shown to agents after completing the test"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQuestionDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleSaveQuestion}
                        variant="contained"
                        disabled={loading}
                    >
                        {editingQuestion ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}